import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  Tab,
  Tabs,
  Button,
} from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { GiftFlowProvider, useGiftContext } from './GiftContext.tsx';
import { GiftNavigationGuard } from './GiftNavigationGuard.tsx';
import { useSearchParams, useLocation, useParams } from 'react-router-dom';
import IocnSuccess from '../../../images/check-success.png';

// EAGER LOAD critical first-tab component for better LCP
import GiftInformationForm from '../../../components/pages/Gift/GIftInformation/GiftInformationForm.tsx';
import Header from '../Header/Header.tsx';
import Footer from '../Footer/Footer.tsx';

// Eager load GiftLoader since it's used for loading states
import GiftLoader from '../../common/GiftLoader';
import StepSkeleton from '../../common/StepSkeleton/StepSkeleton.tsx';

// Lazy load non-critical components
const Message = lazy(() => import('./Message/Message.tsx'));
const Gift = lazy(() => import('./Gift/Gift.tsx'));
const Review = lazy(() => import('./Review/Review.tsx'));
const Delivery = lazy(() => import('./Delivery/Delivery.tsx'));


const tabItems = [
  { id: '1', label: 'Start a Gift', component: null },
  // { id: '2', label: 'Occasion & Date', component: <Occasion /> },
  { id: '3', label: 'Message', component: null },
  { id: '4', label: 'Gift', component: null },
  { id: '5', label: 'Review', component: null },
  { id: '6', label: 'Delivery & Payment', component: null },
];

// Static display numbers for mobile accordion
const getDisplayNumber = (id: string) => {
  if (id === '1') return '1';
  if (id === '3') return '2'; 
  if (id === '4') return '3';
  if (id === '5') return '4';
  if (id === '6') return '5';
  return '1';
};

const StartGiftContent: React.FC = () => {
  const { activeTab, setActiveTab, goToNextTab, goToPreviousTab, loadGift, isLoadingGift, currentGift, completedSteps, isStepAccessible, resetGift } = useGiftContext();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [loadingFailed, setLoadingFailed] = useState<boolean>(false);
  const [hasLoadedInitially, setHasLoadedInitially] = useState<boolean>(false);
  const [currentGiftId, setCurrentGiftId] = useState<string | null>(null);
  const toggleTab = (tab: string) => {

    // Check if the step is accessible
    if (!isStepAccessible(tab)) {
      return; // Prevent navigation to inaccessible steps
    }

    setActiveTab(tab);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check for edit/create mode based on new URL structure
  useEffect(() => {

    // New URL structure: /my-gifts/edit/{giftId} or /my-gifts/create/{giftId}
    const giftId = params.giftId;
    const isEditRoute = location.pathname.includes('/edit/');
    const isCreateRoute = location.pathname.includes('/create/');
    const isLegacyRoute = location.pathname === '/start-gift';

    // Handle new URL structure
    if ((isEditRoute || isCreateRoute) && giftId && (!hasLoadedInitially || currentGiftId !== giftId)) {
      setEditMode(isEditRoute); // Only edit mode if it's /edit/ route
      setLoadingFailed(false);

      const loadWithTimeout = async () => {
        try {
          if (isEditRoute) {
            // Load existing draft for editing
            await loadGift(giftId, true);
          } else {
            // Create mode - initialize with gift ID but don't load (new gift)
            // For create mode, we might want to initialize the gift context with the ID
            // but not load existing data - let the context handle new gift creation
          }
          setHasLoadedInitially(true);
          setCurrentGiftId(giftId);
        } catch (error) {
          setLoadingFailed(true);
        }
      };

      loadWithTimeout();

      // Fallback timeout
      const timeoutId = setTimeout(() => {
        setLoadingFailed(true);
      }, 10000);

      return () => clearTimeout(timeoutId);
    }

    // Handle legacy URL structure (backward compatibility)
    else if (isLegacyRoute) {
      const editModeParam = searchParams.get('editMode');
      const legacyGiftId = searchParams.get('giftId');

      if (editModeParam === 'true' && legacyGiftId && (!hasLoadedInitially || currentGiftId !== legacyGiftId)) {
        setEditMode(true);
        setLoadingFailed(false);

        const loadWithTimeout = async () => {
          try {
            await loadGift(legacyGiftId, true);
            setHasLoadedInitially(true);
            setCurrentGiftId(legacyGiftId);
          } catch (error) {
            setLoadingFailed(true);
          }
        };

        loadWithTimeout();

        const timeoutId = setTimeout(() => {
          setLoadingFailed(true);
        }, 10000);

        return () => clearTimeout(timeoutId);
      } else if (editModeParam !== 'true' && !legacyGiftId) {
        // Fresh start - reset the gift context for a new gift creation
        setEditMode(false);
        resetGift();
        setHasLoadedInitially(false);
        setCurrentGiftId(null);
      }
    }
  }, [location.pathname, params.giftId, searchParams, hasLoadedInitially, currentGiftId, loadGift, resetGift]); // Added resetGift

  // ?step=N lets a caller (e.g. the /my-gifts detail page's "Review & Complete
  // Payment") open a finished draft at a specific step instead of resuming from
  // the server's giftsteps. Applied once, and only after the load has populated
  // completedSteps — until then isStepAccessible would reject the target. If the
  // step never becomes accessible we silently keep the giftsteps-derived tab.
  const requestedStep = searchParams.get('step');
  const [hasAppliedRequestedStep, setHasAppliedRequestedStep] = useState<boolean>(false);

  useEffect(() => {
    if (!requestedStep || hasAppliedRequestedStep || !hasLoadedInitially) {
      return;
    }
    if (!tabItems.some(item => item.id === requestedStep) || !isStepAccessible(requestedStep)) {
      return;
    }
    setActiveTab(requestedStep);
    setHasAppliedRequestedStep(true);
  }, [requestedStep, hasAppliedRequestedStep, hasLoadedInitially, isStepAccessible, setActiveTab]);

  // Monitor loading state changes for debugging
  useEffect(() => {

    // If we have current gift data and we're in edit mode, consider loading successful
    if (editMode && currentGift && currentGift._id && !loadingFailed) {
    }
  }, [isLoadingGift, editMode, currentGift, loadingFailed]);

  // Monitor activeTab changes
  useEffect(() => {
  }, [activeTab, editMode]);

  // Handle next navigation from each component
  const handleNext = () => {
    goToNextTab();
  };

  // Handle previous navigation from each component
  const handlePrevious = () => {
    goToPreviousTab();
  };

  // Render the appropriate component based on active tab
  const renderTabComponent = (tabId: string) => {
    // Critical first tab - NO Suspense for better LCP
    if (tabId === '1') {
      return (
        <div key={tabId} style={{ overflowX: 'hidden' }}>
          <GiftInformationForm onNext={handleNext} />
        </div>
      );
    }

    // Other tabs can be lazy loaded
    const lazyComponents = {
      '3': <Message onNext={handleNext} onPrevious={handlePrevious} />,
      '4': <Gift onNext={handleNext} onPrevious={handlePrevious} />,
      '5': <Review onNext={handleNext} onPrevious={handlePrevious} />,
      '6': <Delivery onPrevious={handlePrevious} />
    };

    const component = lazyComponents[tabId];

    return (
      <Suspense fallback={<StepSkeleton step={tabId} />}>
        <div key={tabId} style={{ overflowX: 'hidden' }}>
          {component}
        </div>
      </Suspense>
    );
  };

  return (
    <div className="gift-creation-page" style={{ overflowX: 'hidden', width: '100%', display: 'flex', flexDirection: 'column', flex: '1' }}>
      {/* Responsive Tabs / Accordion */}
      <section className="top-strip shadow-none py-4">
        <div className="container">
          {!isMobile ? (
            <div className="desktop-stepper-wrapper">
              {/* Desktop: Only show tabs with labels, no step indicator */}
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => k && toggleTab(k)}
                className="my-gifts-tab d-flex flex-wrap"
              >
                {tabItems.map(({ id, label }) => {
                  const isAccessible = isStepAccessible(id);
                  const isCompleted = completedSteps.includes(id);
                  return (
                    <Tab
                      eventKey={id}
                      title={
                        <span
                          className={`${!isAccessible ? "disabled-tab" : ""} ${
                            isCompleted ? "completed-tab1" : ""
                          }`}
                        >
                          {isCompleted && (
                            <img
                              src={IocnSuccess}
                              alt="Completed"
                              className="me-1"
                              style={{ width: "16px", height: "16px" }}
                            />
                          )}
                          {label}
                          {/* {!isAccessible && ' 🔒'} */}
                        </span>
                      }
                      className={`custom-tab-bg ${!isAccessible ? "disabled" : ""}`}
                      key={id}
                      disabled={!isAccessible}
                    ></Tab>

                  );
                })}
              </Tabs>
            </div>
          ) : (
            <div className="mobile-stepper-wrapper">
              {/* Horizontal Step Indicator - Top */}
              <div className="step-indicator-bar">
                {tabItems.map(({ id }, index) => {
                  const isAccessible = isStepAccessible(id);
                  const isCompleted = completedSteps.includes(id);
                  const isActive = activeTab === id;
                  return (
                    <React.Fragment key={id}>
                      <motion.div
                        className="step-item"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: index * 0.05, duration: 0.2 }}
                      >
                        <motion.button
                          onClick={() => isAccessible && setActiveTab(id)}
                          disabled={!isAccessible}
                          className={`step-dot ${
                            isCompleted ? 'completed' :
                            isActive ? 'active' :
                            isAccessible ? 'accessible' : 'disabled'
                          }`}
                          whileTap={isAccessible ? { scale: 0.9 } : {}}
                        >
                          {isCompleted ? (
                            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                              <path d="M1 5L4.5 8.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            <span>{getDisplayNumber(id)}</span>
                          )}
                        </motion.button>
                      </motion.div>
                      {index < tabItems.length - 1 && (
                        <div className={`step-connector ${isCompleted ? 'completed' : ''}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Current Step Content - Only Active Step */}
              <div className="mobile-step-content">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    {renderTabComponent(activeTab)}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Tab content (desktop only) - Removed animations for better LCP */}
      {!isMobile && (
        <div
          className='create-area-block'
          style={{backgroundColor: '#EFF4FF', overflowX: 'hidden', display: 'flex', flexDirection: 'column', flex: '1'}}
        >
          {tabItems.map(({ id }) => (
            activeTab === id && (
              <div key={id} style={{ overflowX: 'hidden', display: 'flex', flexDirection: 'column', flex: '1' }}>
                <div className="container bg-white member-area" style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                  {renderTabComponent(id)}
                </div>
              </div>
            )
          ))}
        </div>
      )}
      
      {/* Loading overlay for edit mode */}
      {editMode && isLoadingGift && !loadingFailed && !currentGift && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
             style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', zIndex: 9999 }}>
          <div className="text-center">
            <GiftLoader
              text="Loading your draft gift..."
              size="large"
              showText={true}
            />
          </div>
        </div>
      )}
      
      {/* Error state for failed loading */}
      {editMode && loadingFailed && (
        <div className="container mt-4">
          <div className="alert alert-warning" role="alert">
            <h5>⚠️ Failed to load draft data</h5>
            <p>We couldn't load your draft. You can either:</p>
            <div className="d-flex gap-2">
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setLoadingFailed(false);
                  const giftId = searchParams.get('giftId');
                  if (giftId) {
                    // Don't reset tab when retrying
                    loadGift(giftId, false);
                  }
                }}
              >
                Try Again
              </button>
              <button 
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setEditMode(false);
                  setLoadingFailed(false);
                  window.history.replaceState({}, '', '/start-gift');
                }}
              >
                Start Fresh
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

// Main component with provider wrapper
const StartGift: React.FC<{ standalone?: boolean }> = ({ standalone = undefined }) => {
  const location = useLocation();
  const [isMobileView, setIsMobileView] = useState<boolean>(false);

  // If standalone is not explicitly provided, determine from URL path
  // New URL structure routes should be standalone (they have their own header/footer)
  const isStandalone = standalone !== undefined ? standalone :
    (location.pathname === '/start-gift' ||
     location.pathname.includes('/my-gifts/create/') ||
     location.pathname.includes('/my-gifts/edit/'));

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <GiftFlowProvider>
      <GiftNavigationGuard>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          {/* Eager load Header for better LCP - it's visible immediately */}
          {isStandalone && <Header activeTab="1" toggle={() => {}} />}

          {/* Main content area that grows to fill space */}
          <div style={{ flex: '1 0 auto' }}>
            <StartGiftContent />
          </div>

          {/* Footer can lazy load - it's below the fold */}
          {/* Hide footer on mobile screens for gift creation flow */}
          {isStandalone && !isMobileView && (
            <Suspense fallback={<div style={{height: '200px', background: '#f5f5f5'}} />}>
              <Footer />
            </Suspense>
          )}
        </div>
      </GiftNavigationGuard>
    </GiftFlowProvider>
  );
};

export default StartGift;